import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link2, Copy, Check, MessageCircle, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { buildWaUrl } from "@/lib/whatsapp";
import { Button } from "@/components/ui/button";

/** "I've paid" link for one client — copy, WhatsApp share, admin/finance reset. */
export function ClientPayLinkRow({ client }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const canReset = ["admin", "finance"].includes(user?.role);
  const { data } = useQuery({
    queryKey: ["client-pay-link", client.id],
    queryFn: () => api.getClientPayLink(client.id),
  });
  const resetMut = useMutation({
    mutationFn: () => api.resetClientPayLink(client.id),
    onSuccess: (d) => {
      qc.setQueryData(["client-pay-link", client.id], d);
      toast.success("Payment link reset — the old link no longer works");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Reset failed"),
  });
  const url = data?.pay_url || "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed — select the link and copy manually");
    }
  };
  const share = () => {
    const msg = `Hi ${client.contact_person || client.name}, if you've already paid a KINOS invoice, ` +
      `you can let us know and attach your transfer receipt here (no login needed):\n${url}\n\nThanks! — KINOS`;
    window.open(buildWaUrl(client.phone, msg), "_blank", "noopener");
  };

  return (
    <div className="sm:col-span-2 border-t border-border pt-3 mt-1" data-testid="client-pay-link-row">
      <div className="flex flex-wrap items-center gap-2">
        <span className="overline flex items-center gap-1"><Link2 className="size-3.5" /> Payment link</span>
        <code className="mono text-xs bg-slate-50 border border-border rounded px-2 py-1 truncate max-w-full sm:max-w-md"
              data-testid="client-pay-link-url" title={url}>
          {url || (data ? "Set PUBLIC_APP_URL to enable" : "…")}
        </code>
        <Button variant="outline" size="sm" data-testid="client-pay-link-copy" disabled={!url} onClick={copy}>
          {copied ? <Check className="size-3.5 mr-1 text-emerald-600" /> : <Copy className="size-3.5 mr-1" />} Copy
        </Button>
        <Button variant="outline" size="sm" className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                data-testid="client-pay-link-whatsapp" disabled={!url} onClick={share}>
          <MessageCircle className="size-3.5 mr-1" /> WhatsApp
        </Button>
        {canReset && (
          <Button variant="ghost" size="sm" className="text-muted-foreground" data-testid="client-pay-link-reset"
                  disabled={resetMut.isPending} onClick={() => resetMut.mutate()}>
            <RotateCcw className="size-3.5 mr-1" /> Reset link
          </Button>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground mt-1.5">
        Clients open this to tell you they&apos;ve paid and attach proof. The same link is in their statement emails.
      </p>
    </div>
  );
}
